<template>
  <v-dialog
    v-model="visible"
    persistent
    max-width="800"
    content-class="pb-detail-dialog"
    @click:outside="close"
  >
    <v-card class="pb-detail-card">
      <v-toolbar color="primary" class="white--text" flat>
        <v-toolbar-title class="font-weight-bold">
          {{ $t("memberDataSection.processingBatchDetail") }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0 pb-detail-dialog__body">
        <div v-if="loading" class="d-flex justify-center align-center py-12">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>

        <template v-else-if="detail">
          <section class="pb-detail-section px-6 pt-5 pb-4">
            <div class="d-flex justify-space-between align-center mb-4">
              <h3 class="pb-detail-section__title mb-0">
                {{ $t("memberDataSection.processingBatch") }}
              </h3>
              <v-btn
                fab
                x-small
                depressed
                color="primary"
                class="pb-detail-section__edit-btn white--text"
                @click="onEdit"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </div>

            <!-- Lot ID / Start Date / End Date -->
            <div class="pb-detail-grid-row">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.purchaseOrderDetail.lotId") }}</div>
                <div class="field-value">{{ detail.batchId }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.startDate") }}</div>
                <div class="field-value">{{ detail.startDate }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.endDate") }}</div>
                <div class="field-value">{{ detail.endDate }}</div>
              </div>
            </div>

            <!-- Lots table -->
            <div class="pb-detail-grid-row--spaced">
              <div class="field-label mb-2">{{ $t("memberDataSection.lots") }}</div>
              <div class="pb-detail-lots">
                <div class="pb-detail-lots__head">
                  <span>{{ $t("farmerLotID") }}</span>
                  <span>{{ $t("weight") }}</span>
                  <span>{{ $t("price") }}</span>
                </div>
                <div
                  v-for="(lot, index) in lotRows"
                  :key="`lot-${index}`"
                  class="pb-detail-lots__row"
                >
                  <span>
                    <div>{{ lot.farmer }}</div>
                    <div class="pb-detail-lots__code">{{ lot.orderCode }}</div>
                  </span>
                  <span>{{ lot.weight }}</span>
                  <span>{{ lot.price }}</span>
                </div>
                <div v-if="!lotRows.length" class="pb-detail-lots__row">
                  <span>—</span>
                  <span>—</span>
                  <span>—</span>
                </div>
              </div>
            </div>

            <!-- Total Coffee Cherry / Air Humidity / Density -->
            <div class="pb-detail-grid-row pb-detail-grid-row--spaced">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.totalCoffeeCherry") }}</div>
                <div class="field-value">{{ detail.totalCoffeeCherryQty }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.airHumidity") }}</div>
                <div class="field-value">{{ detail.humidity }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.density") }}</div>
                <div class="field-value">{{ detail.density }}</div>
              </div>
            </div>

            <!-- Temperature / Bean Moisture / Process Type -->
            <div class="pb-detail-grid-row pb-detail-grid-row--spaced">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.temperature") }}</div>
                <div class="field-value">{{ detail.temperature }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.beanMoistureContent") }}</div>
                <div class="field-value">{{ detail.waterContent }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processType") }}</div>
                <div class="field-value">{{ detail.processType }}</div>
              </div>
            </div>

            <!-- Grade / Season / Bags / Vehicle Plate -->
            <div class="pb-detail-grid-row pb-detail-grid-row--spaced">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.gradeCol") }}</div>
                <div class="field-value">{{ detail.grade }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.seasonCol") }}</div>
                <div class="field-value">{{ detail.season }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.bagsCol") }}</div>
                <div class="field-value">{{ detail.bags }}</div>
              </div>
            </div>

            <div class="pb-detail-grid-row pb-detail-grid-row--spaced">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.vehiclePlateCol") }}</div>
                <div class="field-value">{{ detail.vehiclePlate }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.harvestedCol") }}</div>
                <div class="field-value">{{ detail.harvested }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.pulpedCol") }}</div>
                <div class="field-value">{{ detail.pulped }}</div>
              </div>
            </div>

            <div class="pb-detail-grid-row pb-detail-grid-row--spaced">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.washedCol") }}</div>
                <div class="field-value">{{ detail.washed }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.dryingStartedCol") }}</div>
                <div class="field-value">{{ detail.dryingStarted }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.processingBatchBulkImport.movedToStoreCol") }}</div>
                <div class="field-value">{{ detail.movedToStore }}</div>
              </div>
            </div>

            <!-- Batch Rating / Parchment Produced (completed only) / Status -->
            <div class="pb-detail-grid-row pb-detail-grid-row--spaced">
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.batchRating") }}</div>
                <div class="field-value">{{ detail.batchRating }}</div>
              </div>
              <div v-if="isCompleted" class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.parchmentProduced") }}</div>
                <div class="field-value">{{ detail.parchmentOut }}</div>
              </div>
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.status") }}</div>
                <div class="field-value">
                  <v-btn
                    v-if="isCompleted"
                    x-small
                    rounded
                    min-width="80"
                    depressed
                    class="green-button"
                    height="28"
                  >
                    {{ $t("memberDataSection.completed") }}
                  </v-btn>
                  <v-btn
                    v-else
                    x-small
                    rounded
                    min-width="80"
                    depressed
                    class="orange-button"
                    height="28"
                  >
                    {{ $t("memberDataSection.inProcess") }}
                  </v-btn>
                </div>
              </div>
            </div>

            <!-- Husk Produced (completed only) -->
            <div
              v-if="isCompleted && detail.quantityOfHusk"
              class="pb-detail-grid-row pb-detail-grid-row--spaced"
            >
              <div class="pb-detail-grid-cell">
                <div class="field-label">{{ $t("memberDataSection.huskProducedLabel") }}</div>
                <div class="field-value">{{ detail.quantityOfHusk }}</div>
              </div>
            </div>

            <!-- Processing Done button (in-process only) -->
            <div v-if="!isCompleted" class="pb-detail-grid-row--spaced">
              <v-btn
                color="primary"
                depressed
                height="44"
                class="text-uppercase white--text px-6"
                :disabled="!getModulePermittedActions.put"
                @click="openProcessingDone"
              >
                {{ $t("memberDataSection.processingDone") }}
              </v-btn>
            </div>
          </section>

          <!-- QR Code -->
          <section class="pb-detail-section px-6 py-4">
            <h3 class="pb-detail-plain-title mb-3">
              {{ $t("memberDataSection.greenCoffee.qrCode") }}
            </h3>
            <div class="pb-detail-qr-row">
              <div class="pb-detail-qr-group">
                <div class="pb-detail-qr-block">
                  <div ref="internalQr" class="pb-detail-qr-placeholder">
                    <qrcode
                      v-if="qrCodes.internalQrText"
                      :text="qrCodes.internalQrText"
                      :size="180"
                      error-level="L"
                      class="pb-detail-qr-code"
                    ></qrcode>
                    <v-icon v-else size="48" color="#bdbdbd">mdi-qrcode</v-icon>
                  </div>
                  <p class="pb-detail-qr-id-line mb-0">
                    <span class="pb-detail-qr-id-line__text">
                      {{ $t("memberDataSection.parchmentDelivery.bulkImport.internalId") }}:
                      <template v-if="qrCodes.internalId">{{ qrCodes.internalId }}</template>
                      <span v-else class="pb-detail-qr-id-missing">—</span>
                    </span>
                  </p>
                </div>
                <div class="pb-detail-qr-block">
                  <div ref="externalQr" class="pb-detail-qr-placeholder">
                    <qrcode
                      v-if="qrCodes.externalQrText"
                      :text="qrCodes.externalQrText"
                      :size="180"
                      error-level="L"
                      class="pb-detail-qr-code"
                    ></qrcode>
                    <v-icon v-else size="48" color="#bdbdbd">mdi-qrcode</v-icon>
                  </div>
                  <p class="pb-detail-qr-id-line mb-0">
                    <span class="pb-detail-qr-id-line__text">
                      {{ $t("memberDataSection.greenCoffee.externalId") }}:
                      <template v-if="qrCodes.externalId">{{ qrCodes.externalId }}</template>
                      <span v-else class="pb-detail-qr-id-missing">—</span>
                    </span>
                  </p>
                </div>
              </div>
              <v-btn
                outlined
                color="primary"
                height="36"
                class="pb-detail-qr-download-btn text-uppercase px-5"
                @click="downloadQrCodes"
              >
                {{ $t("memberDataSection.parchmentDelivery.bulkImport.downloadQr") }}
              </v-btn>
            </div>
          </section>

          <!-- Download Report / Quality Control (completed only) -->
          <section v-if="isCompleted" class="pb-detail-section px-6 pt-0 pb-4">
            <v-divider class="pb-detail-section-divider mb-4"></v-divider>
            <div class="pb-detail-actions">
              <v-btn
                color="primary"
                height="36"
                depressed
                class="text-uppercase white--text px-5 mr-3 mb-2"
                :loading="generatingReport"
                :disabled="generatingReport"
                @click="downloadReport"
              >
                {{ $t("memberDataSection.purchaseOrderDetail.downloadReport") }}
              </v-btn>
              <v-btn
                outlined
                color="primary"
                height="36"
                class="text-uppercase px-5 mb-2"
                @click="onQualityControl"
              >
                <v-icon left small>mdi-seal</v-icon>
                {{ $t("memberDataSection.greenCoffee.qualityControlButton") }}
              </v-btn>
            </div>
          </section>
        </template>
      </v-card-text>

      <v-divider v-if="!loading && detail"></v-divider>
      <v-card-actions v-if="!loading && detail" class="px-6 py-4 pb-detail-footer">
        <v-spacer></v-spacer>
        <v-btn
          outlined
          color="primary"
          height="36"
          class="text-uppercase px-5 mr-3"
          @click="onEdit"
        >
          {{ $t("edit") }}
        </v-btn>
        <v-btn
          color="primary"
          height="36"
          depressed
          class="text-uppercase white--text px-5"
          @click="close"
        >
          {{ $t("memberDataSection.greenCoffee.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Processing Done input dialog -->
    <v-dialog v-model="processingDoneDialog" width="800" @click:outside="processingDoneDialog = false">
      <v-card rounded="0">
        <v-toolbar color="primary" class="white--text" flat>
          <v-toolbar-title class="font-weight-bold">
            {{ $t("memberDataSection.processingDone") }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" @click="processingDoneDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="black--text mt-8">
          <v-form ref="processedCoffeeOutForm" autocomplete="off" @submit.prevent="submitProcessingDone">
            <v-row>
              <v-col cols="12" class="py-0 px-4">
                <div class="d-flex align-center pb-output-checkbox-row">
                  <v-checkbox
                    v-model="parchmentProducedChecked"
                    color="primary"
                    hide-details
                    class="mt-0 pt-0 pb-output-checkbox"
                  ></v-checkbox>
                  <span class="pb-output-checkbox-label">{{ $t("memberDataSection.parchmentProduced") }}</span>
                </div>
              </v-col>

              <v-col v-if="parchmentProducedChecked" cols="6" class="py-0 px-4">
                <div class="d-flex justify-space-between align-end">
                  <label>{{ $t("memberDataSection.parchmentProduced") }}</label>
                  <label>KG</label>
                </div>
                <v-text-field
                  v-model="parchmentOut"
                  outlined
                  dense
                  type="number"
                  min="0"
                  :placeholder="$t('enterHere')"
                  :rules="parchmentOutRules"
                ></v-text-field>
              </v-col>

              <v-col cols="12" class="py-0 px-4">
                <div class="d-flex align-center pb-output-checkbox-row">
                  <v-checkbox
                    v-model="huskProducedChecked"
                    color="primary"
                    hide-details
                    class="mt-0 pt-0 pb-output-checkbox"
                  ></v-checkbox>
                  <span class="pb-output-checkbox-label">{{ $t("memberDataSection.huskProduced") }}</span>
                </div>
              </v-col>

              <v-col v-if="huskProducedChecked" cols="6" class="py-0 px-4">
                <div class="d-flex justify-space-between align-end">
                  <label>{{ $t("memberDataSection.huskProducedLabel") }}</label>
                  <label>KG</label>
                </div>
                <v-text-field
                  v-model="quantityOfHusk"
                  outlined
                  dense
                  type="number"
                  min="0"
                  :placeholder="$t('enterHere')"
                  :rules="quantityOfHuskRules"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-card-actions class="mt-10">
              <v-spacer></v-spacer>
              <v-btn outlined color="primary" width="112" @click="processingDoneDialog = false">
                {{ $t("cancel") }}
              </v-btn>
              <v-btn width="112" color="primary" type="submit">
                {{ $t("submit") }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <ProcessingBatchReportPdfGenerator ref="reportPdfGenerator" />

    <QualityGradingDialog
      v-model="qualityGradingVisible"
      :total-quantity="qualityGradingTotalQuantity"
      :unit-size="qualityGradingUnitSize"
      :lots="qualityGradingLots"
      :quality-scores="gradingQualityScores"
      :labels="gradingLabels"
      @finish-grading="onQualityGradingFinished"
      @lots-changed="qualityGradingLots = $event"
    />
  </v-dialog>
</template>

<script>
import moment from "moment";
import BuyingStationService from "@/_services/BuyingStationService";
import loadingMixin from "@/mixins/LoadingMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import ProcessingBatchReportPdfGenerator from "@/components/reports/ProcessingBatchReportPdfGenerator.vue";
import QualityGradingDialog from "./QualityGradingDialog.vue";

export default {
  name: "ProcessingBatchDetailDialog",
  components: { ProcessingBatchReportPdfGenerator, QualityGradingDialog },
  mixins: [loadingMixin, getPermittedActions],
  props: {
    value: { type: Boolean, default: false },
    record: { type: Object, default: null },
  },
  data() {
    return {
      visible: this.value,
      loading: false,
      batch: null,
      processingDoneDialog: false,
      parchmentProducedChecked: false,
      huskProducedChecked: false,
      parchmentOut: "",
      quantityOfHusk: "",
      generatingReport: false,
      qualityGradingVisible: false,
      qualityGradingLots: [],
      gradingQualityScores: [],
      gradingLabels: [],
      savingGrading: false,
    };
  },
  computed: {
    source() {
      return this.batch || this.record || {};
    },
    isCompleted() {
      return this.source.parchmentOut != null;
    },
    parchmentOutRules() {
      if (!this.parchmentProducedChecked) return [];
      return [
        (v) => !!v || this.$t("processedCoffeeOutRequired"),
        (v) =>
          v <= this.source.totalCoffeeCherryQty ||
          "Processed Coffee Out value cannot be greater than total coffee quantity.",
      ];
    },
    quantityOfHuskRules() {
      if (!this.huskProducedChecked) return [];
      return [(v) => !!v || this.$t("memberDataSection.huskProducedRequired")];
    },
    detail() {
      if (!this.batch && !this.record) return null;
      const b = this.source;
      return {
        batchId: b.batchCode || b.id || "—",
        startDate: this.formatDate(b.startDate),
        endDate: this.formatDate(b.endDate),
        totalCoffeeCherryQty:
          b.totalCoffeeCherryQty != null ? `${b.totalCoffeeCherryQty} kg` : "—",
        parchmentOut: b.parchmentOut != null ? `${b.parchmentOut} kg` : "—",
        quantityOfHusk: b.quantityOfHusk != null ? `${b.quantityOfHusk} kg` : "",
        batchRating: b.batchRating || "—",
        processType: b.processingType?.name || "—",
        humidity: b.humidity != null ? `${b.humidity}%` : "—",
        temperature: b.temperature != null ? `${b.temperature} C` : "—",
        density: b.density != null ? `${b.density} kg` : "—",
        waterContent: b.waterContent != null ? `${b.waterContent}%` : "—",
        grade: b.grade || "—",
        season: b.season || "—",
        bags: b.bags != null ? b.bags : "—",
        vehiclePlate: b.vehiclePlate || "—",
        harvested: b.harvested ? this.formatDate(b.harvested) : "—",
        pulped: b.pulped ? this.formatDate(b.pulped) : "—",
        washed: b.washed ? this.formatDate(b.washed) : "—",
        dryingStarted: b.dryingStarted ? this.formatDate(b.dryingStarted) : "—",
        movedToStore: b.movedToStore ? this.formatDate(b.movedToStore) : "—",
      };
    },
    lotRows() {
      const orders = Array.isArray(this.source.buyingStationOrder)
        ? this.source.buyingStationOrder
        : [];
      return orders.map((order) => ({
        farmer: this.formatLotFarmer(order),
        orderCode: order.orderCode || "—",
        weight: order.coffeeCherryQty != null ? `${order.coffeeCherryQty} KG` : "—",
        price: this.formatLotPrice(order),
      }));
    },
    qrCodes() {
      const b = this.source;
      const internalId = this.normalizeId(b.batchCode) || this.normalizeId(b.id);
      // The external label shows the outturn number (not the raw external id).
      const externalId = this.normalizeId(b.outturnNumber);
      return {
        internalId,
        externalId,
        // Internal QR encodes the batch's JSON payload (from the backend) so a scan
        // yields the full details; the label still shows the batch code.
        internalQrText: b.qrCode || internalId || "",
        // External QR encodes the public traceability URL (from the backend) so it
        // opens the trace page; the label shows the outturn number.
        externalQrText: b.externalTraceabilityUrl || "",
      };
    },
    qualityGradingTotalQuantity() {
      const b = this.source;
      return Number(b.parchmentOut ?? b.totalCoffeeCherryQty) || 0;
    },
    qualityGradingUnitSize() {
      const first = (this.qualityGradingLots || []).find((l) => l.unitSize != null);
      return first ? Number(first.unitSize) : 40;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
      if (v) {
        this.loadBatch();
      }
    },
    visible(v) {
      this.$emit("input", v);
      if (!v) {
        this.batch = null;
        this.processingDoneDialog = false;
      }
    },
    record: {
      handler() {
        if (this.visible) {
          this.loadBatch();
        }
      },
      deep: true,
    },
  },
  methods: {
    close() {
      this.visible = false;
      this.$emit("close");
    },
    onEdit() {
      this.$emit("edit", this.record);
    },
    normalizeId(value) {
      const id = value != null ? String(value).trim() : "";
      return id || null;
    },
    formatLotFarmer(order) {
      const person = order.farmer || order.buyingStation || {};
      const name = [person.firstName, person.lastName].filter(Boolean).join(" ");
      return name || "—";
    },
    formatLotPrice(order) {
      if (order.grandTotal == null || order.grandTotal === "") return "—";
      const currency = order.currency || "USD";
      return `${currency} ${order.grandTotal}`;
    },
    formatDate(value) {
      if (!value) return "—";
      if (moment(value, "MM/DD/YYYY", true).isValid()) {
        return moment(value, "MM/DD/YYYY").format("DD-MMM-YYYY").toUpperCase();
      }
      if (moment(value).isValid()) {
        return moment(value).format("DD-MMM-YYYY").toUpperCase();
      }
      return String(value).toUpperCase();
    },
    qrElement(ref) {
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (!el) return null;
      return el.querySelector("canvas") || el.querySelector("img") || null;
    },
    saveDataUrl(dataUrl, filename) {
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    // Draw both QRs (internal + external) into one PNG so a single download
    // reliably includes both — avoids the browser's multi-download prompt.
    downloadQrCodes() {
      const sources = [
        { el: this.qrElement(this.$refs.internalQr), label: this.qrCodes.internalId, title: "Internal ID" },
        { el: this.qrElement(this.$refs.externalQr), label: this.qrCodes.externalId, title: "External ID" },
      ].filter((s) => s.el);

      if (!sources.length) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.parchmentDelivery.bulkImport.downloadQr"),
          text: this.$t("error"),
        });
        return;
      }

      const S = 240; // each QR rendered size in the combined image
      const pad = 28;
      const gap = 48;
      const labelH = 46;
      const canvas = document.createElement("canvas");
      canvas.width = pad * 2 + sources.length * S + (sources.length - 1) * gap;
      canvas.height = pad * 2 + S + labelH;

      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.textAlign = "center";

      sources.forEach((s, i) => {
        const x = pad + i * (S + gap);
        try {
          ctx.drawImage(s.el, x, pad, S, S);
        } catch (e) {
          /* skip a QR that can't be drawn */
        }
        const cx = x + S / 2;
        ctx.fillStyle = "#1a1a1a";
        ctx.font = "600 15px Poppins, Arial, sans-serif";
        ctx.fillText(s.title, cx, pad + S + 20);
        if (s.label) {
          ctx.fillStyle = "#555";
          ctx.font = "13px Poppins, Arial, sans-serif";
          ctx.fillText(String(s.label), cx, pad + S + 40);
        }
      });

      const base = this.detail?.batchId || "processing-batch";
      this.saveDataUrl(canvas.toDataURL("image/png"), `${base}-qr.png`);
    },
    async downloadReport() {
      const payload = this.source;
      if (!payload || !this.$refs.reportPdfGenerator) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.purchaseOrderDetail.downloadReport"),
          text: this.$t("memberDataSection.processingBatchReport.reportDataMissing"),
        });
        return;
      }

      this.generatingReport = true;
      try {
        await this.$refs.reportPdfGenerator.generatePdf(payload, this.record);
        this.$notify({
          type: "success",
          title: this.$t("memberDataSection.purchaseOrderDetail.downloadReport"),
          text: this.$t("memberDataSection.processingBatchReport.downloadReportSuccess"),
        });
      } catch (err) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.purchaseOrderDetail.downloadReport"),
          text:
            err?.message ||
            this.$t("memberDataSection.processingBatchReport.downloadReportFailed"),
        });
      } finally {
        this.generatingReport = false;
      }
    },
    onQualityControl() {
      // Reuse the Create Quality Grade page, prefilled with this batch's gradings.
      const gradings = Array.isArray(this.source.qualityGradings)
        ? this.source.qualityGradings
        : [];
      this.qualityGradingLots = gradings.map((g) => ({
        id: g.id,
        lotId: g.lotId,
        quantity: g.quantity,
        unitSize: g.unitSize,
        qualityScore: g.qualityScore,
      }));
      // Open immediately; grade options (only needed when editing a lot) load in the
      // background so the dialog doesn't wait on the network round-trip.
      this.qualityGradingVisible = true;
      this.loadGradingOptions();
    },
    async loadGradingOptions() {
      try {
        const { data } = await this.$http.get("/coffee/greencoffee/options");
        const payload = data?.data || data || {};
        this.gradingQualityScores = payload.greenCoffeeQuality || [];
        this.gradingLabels = payload.labels || [];
      } catch (err) {
        console.log("Processing batch grading options fetch error", err);
      }
    },
    async onQualityGradingFinished(lots) {
      this.qualityGradingLots = lots || [];
      const b = this.source;
      // Round-trip the batch's own stored fields (idempotent) and only change gradings.
      const payload = {
        batchId: b.id,
        buyingStationId: b.buyingStationId,
        startDate: b.startDate,
        endDate: b.endDate,
        totalCoffeeCherryQty: b.totalCoffeeCherryQty,
        humidity: b.humidity,
        temperature: b.temperature,
        waterContent: b.waterContent,
        processingTypeId: b.processingTypeId,
        batchRating: b.batchRating,
        density: b.density,
        purchasingNum: (b.buyingStationOrder || []).map((o) => o.id),
        qualityGrading: this.qualityGradingLots.map((lot) => ({
          ...(Number.isInteger(lot.id) && { id: lot.id }),
          lotId: lot.lotId,
          quantity: lot.quantity,
          quantityUnit: "kg",
          unitSize: lot.unitSize,
          unitSizeUnit: "kg",
          qualityScore: lot.qualityScore,
        })),
      };
      this.savingGrading = true;
      this.startLoading();
      try {
        const res = await this.$http.put(
          "/coffee/buying-station/processing-batch",
          payload
        );
        if (res.data.success) {
          this.qualityGradingVisible = false;
          this.$notify({
            type: "success",
            title: this.$t("memberDataSection.greenCoffee.finishGrading"),
            text: this.$t("memberDataSection.processingBatchConfirm.qualityGradingFinished"),
          });
          this.$emit("processed");
          await this.loadBatch();
        }
      } catch (error) {
        this.$notify({
          type: "error",
          title: this.$t("memberDataSection.greenCoffee.finishGrading"),
          text: error?.data?.message || error?.message || this.$t("error"),
        });
      } finally {
        this.savingGrading = false;
        this.stopLoading();
      }
    },
    openProcessingDone() {
      this.parchmentProducedChecked = false;
      this.huskProducedChecked = false;
      this.parchmentOut = "";
      this.quantityOfHusk = "";
      this.processingDoneDialog = true;
    },
    async submitProcessingDone() {
      if (!this.parchmentProducedChecked && !this.huskProducedChecked) {
        this.$notify({
          type: "error",
          text: this.$t("memberDataSection.processingDoneSelectionRequired"),
        });
        return;
      }
      if (!this.$refs.processedCoffeeOutForm.validate()) return;

      const payload = {};
      if (this.parchmentProducedChecked) {
        payload.parchmentOut = Number(this.parchmentOut);
      }
      if (this.huskProducedChecked) {
        payload.wasHuskProduced = true;
        payload.quantityOfHusk = Number(this.quantityOfHusk);
      }

      this.startLoading();
      try {
        const res = await this.$http.put(
          `/coffee/buying-station/processing-batch/${this.source.batchCode || this.source.id}`,
          payload
        );

        if (res.data.success) {
          this.processingDoneDialog = false;
          this.$notify({
            type: "success",
            title: this.$t("success"),
          });
          this.$emit("processed");
          await this.loadBatch();
        }
      } catch (error) {
        this.$notify({
          title: "Error updating processed coffee out.",
          type: "error",
        });
      } finally {
        this.stopLoading();
      }
    },
    mergeBatchWithRecord(apiData, record) {
      const row = record || {};
      const api = apiData || {};
      const apiOrders = Array.isArray(api.buyingStationOrder) ? api.buyingStationOrder : [];
      const rowOrders = Array.isArray(row.buyingStationOrder) ? row.buyingStationOrder : [];

      return {
        ...row,
        ...api,
        batchCode: api.batchCode || row.batchCode,
        id: api.id ?? row.id,
        buyingStationOrder: apiOrders.length ? apiOrders : rowOrders,
        processingType: api.processingType || row.processingType,
      };
    },
    async loadBatch() {
      const batchId = this.record?.batchCode || this.record?.id;
      if (!batchId) {
        this.batch = this.record;
        return;
      }
      this.loading = true;
      try {
        const data = await BuyingStationService.getProcessBatch(batchId);
        if (data.code === 200) {
          this.batch = this.mergeBatchWithRecord(data.data, this.record);
        } else {
          this.batch = this.mergeBatchWithRecord(this.record, this.record);
        }
      } catch (err) {
        this.batch = this.mergeBatchWithRecord(this.record, this.record);
        this.$notify({
          type: "error",
          text: err?.data?.message || err?.message || this.$t("error"),
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .pb-detail-dialog {
  overflow-x: hidden;
}

.pb-output-checkbox-row {
  width: fit-content;
}

::v-deep .pb-output-checkbox {
  flex: 0 0 auto;
}

.pb-output-checkbox-label {
  margin-left: -4px;
  user-select: none;
}

.pb-detail-card {
  border-radius: 4px;
  overflow: hidden;
  font-family: "Poppins", sans-serif;
}

.pb-detail-dialog__body {
  max-height: calc(90vh - 140px);
  overflow-y: auto;
  overflow-x: hidden;
  background: #fff;
}

.pb-detail-section + .pb-detail-section {
  padding-top: 8px;
}

.pb-detail-section__title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 700;
  font-size: 16px;
  line-height: 1.35;
}

.pb-detail-section__edit-btn {
  box-shadow: none;
}

.pb-detail-plain-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
}

.pb-detail-grid-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  column-gap: 20px;
  row-gap: 16px;
}

.pb-detail-grid-row--spaced {
  margin-top: 20px;
}

.pb-detail-grid-cell {
  min-width: 0;
}

.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.35;
  margin-bottom: 2px;
}

.field-value {
  font-size: 16px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.4;
  word-break: break-word;
}

.pb-detail-lots {
  max-width: 420px;
}

.pb-detail-lots__head,
.pb-detail-lots__row {
  display: grid;
  grid-template-columns: 1.6fr 1fr 1fr;
  column-gap: 16px;
  align-items: center;
}

.pb-detail-lots__head {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  padding-bottom: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.pb-detail-lots__row {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.pb-detail-lots__code {
  color: rgba(0, 0, 0, 0.6);
}

.pb-detail-section-divider {
  border-color: #e0e0e0;
}

.pb-detail-qr-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
}

.pb-detail-qr-group {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 16px;
  min-width: 0;
}

.pb-detail-qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  width: max-content;
}

.pb-detail-qr-download-btn {
  flex-shrink: 0;
  align-self: center;
}

.pb-detail-qr-placeholder {
  width: 180px;
  height: 180px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.pb-detail-qr-placeholder ::v-deep img,
.pb-detail-qr-placeholder ::v-deep canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.pb-detail-qr-id-line {
  margin: 0;
  text-align: center;
}

.pb-detail-qr-id-line__text {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.35;
  word-break: break-word;
  max-width: 120px;
  text-align: center;
}

.pb-detail-qr-id-missing {
  color: rgba(0, 0, 0, 0.38);
  font-weight: 400;
}

.pb-detail-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
}

.pb-detail-footer {
  background: #fff;
}

.green-button {
  background-color: #d4edda !important;
  border: 2px solid #28a745 !important;
  color: #28a745 !important;
}

.orange-button {
  background-color: #fff7ec !important;
  border: 2px solid #ffa826 !important;
  color: #ffa826 !important;
}

@media (max-width: 600px) {
  .pb-detail-grid-row {
    grid-template-columns: 1fr;
  }

  .pb-detail-qr-row {
    flex-wrap: wrap;
  }
}
</style>
